const CONSENT_KEY = "chessla.analytics-consent";
export const ANALYTICS_CONSENT_EVENT = "chessla:analytics-consent";
const measurementId =
  import.meta.env.VITE_GA4_MEASUREMENT_ID ??
  (import.meta.env.PROD && globalThis.location?.hostname === "app.chessla.net" ? "G-1Z1131LZ02" : "");
let lastPage: string | null = null;

export type ProductEvent =
  | "signup_completed"
  | "import_completed"
  | "trial_games_selected"
  | "analysis_completed"
  | "nikola_read_generated"
  | "patterns_viewed"
  | "club_checkout_started"
  | "club_yearly_upgrade_started"
  | "club_subscription_activated";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

export const analyticsConsent = (): "granted" | "denied" | null => {
  const value = localStorage.getItem(CONSENT_KEY);
  return value === "granted" || value === "denied" ? value : null;
};

const loadGa4 = () => {
  if (!measurementId) return;
  // The existing Google tag is linked to legacy duplicate destinations.
  for (const id of ["G-1Z1131LZ02", "G-WQP0WT096C", "G-KKTHH1B7GR"]) {
    if (id !== measurementId) window[`ga-disable-${id}`] = true;
  }
  window[`ga-disable-${measurementId}`] = false;
  if (document.querySelector(`script[data-chessla-ga4="${measurementId}"]`)) {
    window.gtag?.("consent", "update", { analytics_storage: "granted" });
    trackAnalyticsPage();
    return;
  }
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.dataset.chesslaGa4 = measurementId;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function () {
    // Google's command queue requires Arguments, not an Array.
    // eslint-disable-next-line prefer-rest-params -- Google requires an Arguments object, not a rest-parameter Array.
    window.dataLayer?.push(arguments);
  };
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("consent", "update", { analytics_storage: "granted" });
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    send_page_view: false,
    page_title: "CHESSLA",
    page_referrer: "",
  });
  trackAnalyticsPage();
  document.head.appendChild(script);
};

/** Never send game IDs, chat IDs, URL queries, or account-specific titles. */
export const trackAnalyticsPage = (pathname = window.location.pathname) => {
  if (analyticsConsent() !== "granted" || !measurementId || !window.gtag) return;
  const section = pathname.split("/")[1];
  const path = [
    "home",
    "games",
    "patterns",
    "trends",
    "chats",
    "chat",
    "analyze",
    "archive",
    "board",
    "profile",
    "settings",
    "subscription",
    "about",
    "help",
    "login",
    "signup",
    "reset-password",
  ].includes(section)
    ? `/${section}`
    : "/";
  if (lastPage === path) return;
  lastPage = path;
  window.gtag("set", { page_location: `${window.location.origin}${path}`, page_title: "CHESSLA", page_referrer: "" });
  window.gtag("event", "page_view", {
    page_location: `${window.location.origin}${path}`,
    page_title: "CHESSLA",
    page_referrer: "",
  });
};

export const setAnalyticsConsent = (value: "granted" | "denied") => {
  localStorage.setItem(CONSENT_KEY, value);
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
  if (value === "granted") loadGa4();
  else {
    lastPage = null;
    window[`ga-disable-${measurementId}`] = true;
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
    if (typeof document !== "undefined")
      for (const cookie of document.cookie.split(";")) {
        const name = cookie.trim().split("=")[0];
        if (!/^_ga(?:_|$)/.test(name)) continue;
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.chessla.net; SameSite=Lax`;
      }
  }
};

export const initializeAnalytics = () => {
  if (analyticsConsent() === "granted") loadGa4();
};

/** Product events deliberately permit no identifiers, chess data, or free text. */
export const trackProductEvent = (event: ProductEvent) => {
  if (analyticsConsent() !== "granted" || !measurementId || !window.gtag) return;
  window.gtag("event", event);
};
